import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import PhotoUpload from './components/PhotoUpload';
import LocationCapture from './components/LocationCapture';

const ServiceRequestForm = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const driverData = routerLocation?.state || {};

  const [formData, setFormData] = useState({
    driverPhone: driverData?.driverPhone || '',
    unitNumber: driverData?.unitNumber || '',
    vin: driverData?.vin || '',
    yardName: driverData?.yardName || '',
    urgency: '',
    issueType: '',
    description: '',
    partsNeeded: '',
    currentMileage: '',
    engineHours: ''
  });

  const [photos, setPhotos] = useState([]);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!driverData?.driverPhone) {
      navigate('/');
    }
  }, [driverData, navigate]);

  const urgencyOptions = [
    { value: 'Emergency', label: 'Emergency', description: 'Immediate attention required' },
    { value: 'Scheduled', label: 'Scheduled', description: 'Can be scheduled for later' }
  ];

  const issueTypeOptions = [
    { value: 'Mechanical', label: 'Mechanical', description: 'Engine, transmission, brakes' },
    { value: 'Electrical', label: 'Electrical', description: 'Lights, battery, wiring' },
    { value: 'Safety', label: 'Safety', description: 'Safety equipment issues' },
    { value: 'Routine Maintenance', label: 'Routine Maintenance', description: 'Oil change, inspection' },
    { value: 'Other', label: 'Other', description: 'Other issues' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.urgency) newErrors.urgency = 'Please select urgency level';
    if (!formData?.issueType) newErrors.issueType = 'Please select issue type';
    if (!formData?.description?.trim()) newErrors.description = 'Please describe the issue';
    if (formData?.description?.length < 10) newErrors.description = 'Description must be at least 10 characters';
    if (!formData?.currentMileage) newErrors.currentMileage = 'Current mileage is required';
    if (formData?.currentMileage && isNaN(Number(formData?.currentMileage))) newErrors.currentMileage = 'Must be a valid number';
    if (!formData?.engineHours) newErrors.engineHours = 'Engine hours is required';
    if (formData?.engineHours && isNaN(Number(formData?.engineHours))) newErrors.engineHours = 'Must be a valid number';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload photos to storage
      const photoUrls = [];
      for (const photo of photos) {
        const fileName = `${Date.now()}-${photo?.name}`;
        const filePath = `${formData?.driverPhone?.replace(/\D/g, '')}/${fileName}`;

        const { error: uploadError } = await supabase?.storage
          ?.from('service-request-photos')
          ?.upload(filePath, photo);

        if (!uploadError) {
          photoUrls?.push(filePath);
        }
      }

      // Submit service request via RPC
      const { data, error } = await supabase?.rpc('create_service_request', {
        p_driver_phone: formData?.driverPhone,
        p_unit_number: formData?.unitNumber,
        p_vin: formData?.vin || null,
        p_yard_name: formData?.yardName || null,
        p_urgency: formData?.urgency,
        p_issue_type: formData?.issueType,
        p_description: formData?.description,
        p_parts_needed: formData?.partsNeeded || null,
        p_photo_urls: photoUrls,
        p_gps_latitude: gpsLocation?.latitude || null,
        p_gps_longitude: gpsLocation?.longitude || null,
        p_gps_address: gpsLocation?.address || null,
        p_current_mileage: formData?.currentMileage ? parseInt(formData?.currentMileage, 10) : null,
        p_engine_hours: formData?.engineHours ? parseInt(formData?.engineHours, 10) : null
      });

      if (error) {
        throw new Error(error?.message || 'Failed to submit service request');
      }

      if (data?.success) {
        navigate('/service-request-confirmation', {
          state: {
            requestId: data?.request_id,
            requestNumber: data?.request_number,
            ...formData,
            photoCount: photoUrls?.length,
            location: gpsLocation
          }
        });
      } else {
        throw new Error(data?.message || 'Failed to submit service request');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setErrors({ submit: err?.message || 'An error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const completionPercentage = () => {
    const fields = ['urgency', 'issueType', 'description', 'currentMileage', 'engineHours'];
    const completed = fields?.filter(field => formData?.[field])?.length;
    return Math.round((completed / fields?.length) * 100);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/')}
              >
                Back to Lookup
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full" style={{ backgroundColor: 'rgba(6,83,149,0.1)' }}>
                    <Icon name="Wrench" size={24} strokeWidth={2} style={{ color: '#065395' }} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-foreground">Request Service</h1>
                    <p className="text-sm text-muted-foreground">Submit a service request for your unit</p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Form Completion</span>
                  <span className="font-medium text-foreground">{completionPercentage()}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionPercentage()}%`, backgroundColor: '#065395' }}
                  />
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="User" size={20} className="mr-2" />
                  Driver Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Driver Phone"
                    value={formData?.driverPhone}
                    disabled
                    className="bg-muted/50"
                  />
                  <Input
                    label="Unit Number"
                    value={formData?.unitNumber}
                    disabled
                    className="bg-muted/50"
                  />
                  <Input
                    label="VIN"
                    value={formData?.vin}
                    disabled
                    className="bg-muted/50"
                  />
                  <Input
                    label="Yard Location"
                    value={formData?.yardName}
                    disabled
                    className="bg-muted/50"
                  />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="AlertCircle" size={20} className="mr-2" />
                  Service Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Service Urgency <span className="text-destructive">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {urgencyOptions?.map((option) => {
                        const isEmergency = option?.value === 'Emergency';
                        const isSelected = formData?.urgency === option?.value;
                        return (
                          <button
                            key={option?.value}
                            type="button"
                            onClick={() => handleInputChange('urgency', option?.value)}
                            className={`flex items-start space-x-3 p-3 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? isEmergency
                                  ? 'border-[#EA2A31] bg-[#EA2A31]/5'
                                  : 'border-[#065395] bg-[#065395]/5' :'border-border hover:border-muted-foreground/40'
                            }`}
                          >
                            <div
                              className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 mt-0.5"
                              style={{
                                backgroundColor: isSelected
                                  ? isEmergency ? 'rgba(234,42,49,0.15)' : 'rgba(6,83,149,0.15)'
                                  : 'rgba(0,0,0,0.05)'
                              }}
                            >
                              <Icon
                                name={isEmergency ? 'AlertTriangle' : 'Clock'}
                                size={16}
                                strokeWidth={2}
                                style={{ color: isSelected ? (isEmergency ? '#EA2A31' : '#065395') : 'var(--color-muted-foreground)' }}
                              />
                            </div>
                            <div>
                              <p
                                className="text-sm font-medium"
                                style={{ color: isSelected ? (isEmergency ? '#EA2A31' : '#065395') : 'var(--color-foreground)' }}
                              >
                                {option?.label}
                              </p>
                              <p className="text-xs text-muted-foreground">{option?.description}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {errors?.urgency && <p className="text-sm text-destructive mt-1">{errors?.urgency}</p>}
                  </div>

                  <Select
                    label="Issue Type"
                    placeholder="Select issue type"
                    options={issueTypeOptions}
                    value={formData?.issueType}
                    onChange={(value) => handleInputChange('issueType', value)}
                    error={errors?.issueType}
                    required
                  />

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Description / Notes <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Describe the issue in detail..."
                      value={formData?.description}
                      onChange={(e) => handleInputChange('description', e?.target?.value)}
                      maxLength={500}
                    />
                    <div className="flex justify-between mt-1">
                      <p className="text-sm text-destructive">{errors?.description}</p>
                      <p className="text-xs text-muted-foreground">{formData?.description?.length || 0}/500</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Current Mileage <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="e.g. 125000"
                        value={formData?.currentMileage}
                        onChange={(e) => handleInputChange('currentMileage', e?.target?.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      {errors?.currentMileage && (
                        <p className="text-sm text-destructive mt-1">{errors?.currentMileage}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Engine Hours <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="e.g. 4500"
                        value={formData?.engineHours}
                        onChange={(e) => handleInputChange('engineHours', e?.target?.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      {errors?.engineHours && (
                        <p className="text-sm text-destructive mt-1">{errors?.engineHours}</p>
                      )}
                    </div>
                  </div>

                  <Input
                    label="Parts or Supplies Needed (Optional)"
                    placeholder="List any parts or supplies needed"
                    value={formData?.partsNeeded}
                    onChange={(e) => handleInputChange('partsNeeded', e?.target?.value)}
                  />
                </div>
              </div>

              <PhotoUpload photos={photos} setPhotos={setPhotos} />

              <LocationCapture location={gpsLocation} setLocation={setGpsLocation} />

              {errors?.submit && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-error">Submission Error</p>
                      <p className="text-sm text-error/80 mt-1">{errors?.submit}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/')}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-white rounded-md shadow-elevation-1 transition-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#065395' }}
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="Loader2" size={18} strokeWidth={2} className="animate-spin" color="white" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <Icon name="Send" size={18} strokeWidth={2} color="white" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServiceRequestForm;